import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  /** Red confirm button for destructive actions */
  danger?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  label: string;
  placeholder?: string;
  initialValue?: string;
  /** The confirm button stays disabled until something is typed */
  required?: boolean;
  multiline?: boolean;
}

export interface DialogRequest {
  options: PromptOptions | ConfirmOptions;
  isPrompt: boolean;
  resolve: (value: boolean | string | null) => void;
}

/**
 * In-app replacements for window.confirm() and window.prompt(): styled like the rest of
 * the app, keyboard accessible, and they don't freeze the page (or a test browser).
 *
 *   if (!(await this.dialogs.confirm({ title: 'Delete template?', danger: true }))) return;
 *   const reason = await this.dialogs.prompt({ title: 'Decline order', label: 'Reason', required: true });
 */
@Injectable({ providedIn: 'root' })
export class LbConfirmService {
  readonly current$ = new BehaviorSubject<DialogRequest | null>(null);
  private queue: DialogRequest[] = [];

  confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise(resolve => this.enqueue({ options, isPrompt: false, resolve: v => resolve(v === true) }));
  }

  /** Resolves with the trimmed text, or null when cancelled. */
  prompt(options: PromptOptions): Promise<string | null> {
    return new Promise(resolve => this.enqueue({ options, isPrompt: true, resolve: v => resolve(typeof v === 'string' ? v : null) }));
  }

  /** Called by the host component when the open dialog is answered. */
  answer(value: boolean | string | null): void {
    const request = this.current$.value;
    if (!request) return;
    this.current$.next(null);
    request.resolve(value);
    const next = this.queue.shift();
    if (next) setTimeout(() => this.current$.next(next));
  }

  private enqueue(request: DialogRequest): void {
    if (this.current$.value) this.queue.push(request);
    else this.current$.next(request);
  }
}
